import { inject } from "@angular/core";
import { RedirectCommand, ResolveFn, Router } from "@angular/router";
import { firstValueFrom, timeout } from "rxjs";
import { IMentionDiscussionEntry } from "@app/shared/general/mention-discussion/resolvers/mention-discussion-access.resolver";
import {
  CHAT_MESSAGE_QUERY_PARAM,
  RESOURCE_CHAT_LINK_STATE,
  RESOURCE_CHAT_OBJECT_ID,
  resourceChatDiscussionCommands,
} from "../helpers/resource-chat-link.helper";
import { ResourceChatRegistryService } from "../services/resource-chat-registry.service";

const ACCESS_TIMEOUT_MS = 20_000;

/**
 * Resolves `/chat/messages/:resourceName/:resourceId/:messageId`, the link every
 * mention email carries, without knowing anything about the resource's pages.
 *
 * The host's access query decides: a viewer who may see the resource goes to its
 * canonical view with the message; anyone else goes to the message's discussion,
 * which checks the current mention itself. Nothing about the resource is fetched
 * or shown here. Only an unknown link or a failed check stays on this page.
 */
export const resourceChatLinkResolver: ResolveFn<IMentionDiscussionEntry> = async route => {
  const router = inject(Router);
  const registry = inject(ResourceChatRegistryService);
  const resource = { resourceName: route.paramMap.get("resourceName") ?? "", resourceId: route.paramMap.get("resourceId") ?? "" };
  const messageId = route.paramMap.get("messageId") ?? "";
  const definition = registry.get(resource.resourceName);
  if (!definition || !RESOURCE_CHAT_OBJECT_ID.test(resource.resourceId) || !RESOURCE_CHAT_OBJECT_ID.test(messageId)) {
    return { resource, access: null, state: "unavailable" };
  }

  try {
    const access = await firstValueFrom(registry.access(resource.resourceName, resource.resourceId).pipe(timeout(ACCESS_TIMEOUT_MS)));
    if (!access.resourceAllowed) {
      return new RedirectCommand(router.createUrlTree(resourceChatDiscussionCommands({ ...resource, messageId })), { replaceUrl: true });
    }
    const view = router.createUrlTree(definition.route(resource.resourceId), { queryParams: { [CHAT_MESSAGE_QUERY_PARAM]: messageId } });
    return new RedirectCommand(view, { replaceUrl: true, state: { [RESOURCE_CHAT_LINK_STATE]: messageId } });
  } catch {
    return { resource, access: null, state: "error" };
  }
};
